import { useMemo } from "react";
import { useFormContext } from "react-hook-form";

import { useGetMaterialData } from "../../hooks/react-query/useMaterialData";
import { useGetFinancialPeriodDetails } from "../../hooks/react-query/useFinancialsData";

// Looks up the per-location price for a material from its pricesByLocation map.
// Returns null when no price is configured for that location.
const priceFor = (material, locationId) => {
  if (!material || !locationId) return null;
  const price = material.pricesByLocation?.[locationId];
  return price != null ? Number(price) : null;
};

// Centralizes loading the tree/material/financialPeriod entities a quote
// references so the calculation engine has live data.
//
// `useGetMaterialData` returns { trees, materials } from /materials/all where
// each row carries pricesByLocation (a map keyed by locationId). The calculator
// then resolves the price for the currently-selected location.
export const useQuoteEntities = () => {
  const { watch } = useFormContext();

  const treeId = watch("treeId");
  const materialId = watch("materialId");
  const partWaxId = watch("partWaxId");
  const treeWaxId = watch("treeWaxId");
  const locationId = watch("locationId");
  const financialPeriodId = watch("financialPeriodId");

  // Loaded materials list (cached client-side via useMaterialListWithCache)
  const { data: materialsData } = useGetMaterialData(true);
  const trees = materialsData?.trees || [];
  const materials = materialsData?.materials || [];

  // Selected financial period (historical-cost block only — wax costs now come
  // from the partWax/treeWax materials below)
  const { data: financialPeriod } = useGetFinancialPeriodDetails(
    financialPeriodId ? Number(financialPeriodId) : null,
  );

  return useMemo(() => {
    const tree = trees.find((t) => t.id === Number(treeId));
    const material = materials.find((m) => m.id === Number(materialId));
    const partWax = materials.find((m) => m.id === Number(partWaxId));
    const treeWax = materials.find((m) => m.id === Number(treeWaxId));

    const loc = locationId ? Number(locationId) : null;

    return {
      tree,
      material,
      partWax,
      treeWax,
      // Per-location prices resolved from each material's pricesByLocation map.
      // Re-derived whenever the user picks a different material or location.
      materialPrice: priceFor(material, loc),
      partWaxPrice: priceFor(partWax, loc),
      treeWaxPrice: priceFor(treeWax, loc),
      financialPeriod,
    };
  }, [
    trees,
    materials,
    treeId,
    materialId,
    partWaxId,
    treeWaxId,
    locationId,
    financialPeriod,
  ]);
};

export default useQuoteEntities;
